import axios from 'axios';

const CHAT_API_URL = process.env.REACT_APP_CHAT_API_URL || 'http://localhost:30090';

const chatApi = axios.create({
    baseURL: CHAT_API_URL,
    headers: {
        'Content-Type': 'application/json'
    }
});

// 요청 인터셉터 - 인증 토큰 추가
chatApi.interceptors.request.use((config) => {
    const token = sessionStorage.getItem('accessToken');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    // 사용자 정보 헤더 추가 (개발용)
    const userId = sessionStorage.getItem('userId') || 'mock-user-001';
    config.headers['X-User-Id'] = userId;
    return config;
});

export const ChatRepository = {
    // 채팅방 목록 조회
    getRoomList: async (userId: string) => {
        try {
            const response = await chatApi.get(`/api/chat/rooms`, {
                params: { userId }
            });
            return response;
        } catch (error) {
            console.error('채팅방 목록 조회 실패:', error);
            // Mock 데이터 반환 (개발용)
            return {
                data: {
                    list: [
                        {
                            roomId: 'ROOM-001',
                            roomName: '금요일 저녁 회식',
                            roomType: 'MEETING',
                            lastMessage: '다들 금요일에 봐요~',
                            lastMessageTime: new Date(Date.now() - 3600000).toISOString(),
                            unreadCount: 3,
                            participantCount: 5,
                            meetingId: 'MTG-001'
                        },
                        {
                            roomId: 'ROOM-002',
                            roomName: '주말 독서모임',
                            roomType: 'MEETING',
                            lastMessage: '이번 주 책은 뭔가요?',
                            lastMessageTime: new Date(Date.now() - 7200000).toISOString(),
                            unreadCount: 0,
                            participantCount: 8,
                            meetingId: 'MTG-002'
                        },
                        {
                            roomId: 'ROOM-003',
                            roomName: '한강 러닝 크루',
                            roomType: 'MEETING',
                            lastMessage: '내일 아침 7시 뚝섬에서 만나요!',
                            lastMessageTime: new Date(Date.now() - 1800000).toISOString(),
                            unreadCount: 5,
                            participantCount: 12,
                            meetingId: 'MTG-003'
                        }
                    ]
                }
            };
        }
    },

    // 채팅방 정보 조회
    getRoomInfo: async (roomId: string) => {
        try {
            const response = await chatApi.get(`/api/chat/rooms/${roomId}`);
            return response;
        } catch (error) {
            console.error('채팅방 정보 조회 실패:', error);
            // Mock 데이터 반환
            return {
                data: {
                    roomId,
                    roomName: '테스트 채팅방',
                    roomType: 'MEETING',
                    participantCount: 5,
                    meetingId: 'MTG-001'
                }
            };
        }
    },

    // 채팅 메시지 조회
    getMessages: async (roomId: string, params: { page: number; pageSize: number }) => {
        try {
            const response = await chatApi.get(`/api/chat/rooms/${roomId}/messages`, { params });
            return response;
        } catch (error) {
            console.error('채팅 메시지 조회 실패:', error);
            // Mock 데이터 반환
            return {
                data: {
                    list: [
                        {
                            messageId: 'MSG-001',
                            senderId: 'USER-001',
                            senderNickname: '김위즈',
                            content: '안녕하세요!',
                            messageType: 'TEXT',
                            createdAt: new Date(Date.now() - 3600000).toISOString()
                        },
                        {
                            messageId: 'MSG-002',
                            senderId: 'USER-002',
                            senderNickname: '이수진',
                            content: '반갑습니다~',
                            messageType: 'TEXT',
                            createdAt: new Date(Date.now() - 3000000).toISOString()
                        },
                        {
                            messageId: 'MSG-003',
                            senderId: 'mock-user-001',
                            senderNickname: '테스트유저',
                            content: '저도 반가워요!',
                            messageType: 'TEXT',
                            createdAt: new Date(Date.now() - 2400000).toISOString()
                        }
                    ]
                }
            };
        }
    },

    // 메시지 전송 (REST API fallback)
    sendMessage: async (roomId: string, message: {
        senderId: string;
        senderNickname: string;
        content: string;
        messageType: string;
    }) => {
        try {
            const response = await chatApi.post(`/api/chat/rooms/${roomId}/messages`, message);
            return response;
        } catch (error) {
            console.error('메시지 전송 실패:', error);
            throw error;
        }
    },

    // 채팅방 생성 (모임용)
    createMeetingRoom: async (meetingId: string, meetingName: string) => {
        try {
            const response = await chatApi.post(`/api/chat/rooms/meeting`, {
                meetingId,
                roomName: meetingName
            });
            return response;
        } catch (error) {
            console.error('채팅방 생성 실패:', error);
            throw error;
        }
    },

    // 채팅방 참여자 목록 조회
    getParticipants: async (roomId: string) => {
        try {
            const response = await chatApi.get(`/api/chat/rooms/${roomId}/participants`);
            return response;
        } catch (error) {
            console.error('참여자 목록 조회 실패:', error);
            return {
                data: {
                    list: [
                        { userId: 'USER-001', nickname: '김위즈', profileUrl: '', isOnline: true },
                        { userId: 'USER-002', nickname: '이수진', profileUrl: '', isOnline: false },
                        { userId: 'mock-user-001', nickname: '테스트유저', profileUrl: '', isOnline: true }
                    ]
                }
            };
        }
    },

    // 읽음 처리
    markAsRead: async (roomId: string, userId: string) => {
        try {
            const response = await chatApi.post(`/api/chat/rooms/${roomId}/read`, { userId });
            return response;
        } catch (error) {
            console.error('읽음 처리 실패:', error);
        }
    }
};
